import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { User } from '@shared/models/user';
import { environment } from '@environments/environment';
import { shareReplay, tap } from 'rxjs';
import { Company } from '@shared/models/company';
import { CompanyAccessType } from '@shared/models/company-access';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  public readonly fetchUser$ = inject(HttpClient).get<User>(`${environment.apiGatewayUrl}/mobiclear/user/me`).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
    tap( user => this.currentUser.set(user))
  );

  public readonly isDeregistrationsUser = computed(() => this.currentUser()!.access.deregistrations);
  public readonly isSecureTransportGen = computed(() => this.currentUser()!.access.secureTransportGeneration);
  public readonly isSecureTransportVal = computed(() => this.currentUser()!.access.secureTransportValidation);
  public readonly isAdministrationUser = computed(() => this.currentUser()!.access.administration);
  public readonly isPickUpUser = computed(() => this.currentUser()!.access.pickUp);
  public readonly isVerifiedTransportUser = computed(() => this.currentUser()!.access.verifiedTransport);
  public readonly isTransportCompany = computed(() => this.currentUser()?.userType === 'TRANSPORT_COMPANY');
  public readonly isFleetOwner = computed(() => this.currentUser()?.userType === 'FLEET_OWNER');
  public readonly isDealer = computed(() => this.currentUser()?.userType === 'DEALER');
  public readonly userType = computed(() => this.currentUser()?.userType);
  public readonly userEmail = computed(() => this.currentUser()?.email);
  public readonly isDataImportUser = computed(
    () => this.isFleetOwner() && (this.isDeregistrationsUser() || this.isVerifiedTransportUser())
  );

  public readonly companiesDeregistrations = computed(() => this.filterCompanyAccess('DEREGISTRATIONS'));
  public readonly companiesManagement = computed(() => this.filterCompanyAccess('ADMINISTRATION'));
  public readonly companiesVerifiedTransport = computed(() => this.filterCompanyAccess('SECURE_TRANSPORT'));
  public readonly companiesDealer = computed(() => this.filterCompanyAccess('DEALER'));

  private readonly currentUser = signal<User | null>(null);

  private filterCompanyAccess(access: CompanyAccessType): Company[] {
    return this.currentUser()!.companiesAccess
      .filter(entry => entry.access === access)
      .flatMap(entry => entry.companies);
  }

}
